// ./src/styles/theme/fonts.ts
// ============================================
// CarExpenses Typography System
// Bold, technical, data-driven aesthetic
// ============================================

export const fonts = {
  // ============================================
  // Font Families
  // ============================================

  // Bricolage Grotesque - Display/Headings
  // Bold, characterful, modern automotive feel
  // Distinctive without being gimmicky
  display: "'Bricolage Grotesque', 'Space Grotesk', -apple-system, sans-serif",

  // Manrope - Body text
  // Clean, geometric, excellent for data-heavy interfaces
  // Great x-height for readability
  primary: "'Manrope', 'DM Sans', -apple-system, 'Segoe UI', sans-serif",

  // JetBrains Mono - Numbers, data, codes
  // Technical, precise, perfect for odometer readings,
  // expense amounts, and vehicle data
  monospace: "'JetBrains Mono', 'Fira Code', 'Source Code Pro', monospace",

  // ============================================
  // Font Sizes (using modular scale - ratio 1.25)
  // ============================================
  sizes: {
    xs: "0.75rem", // 12px - Captions, timestamps
    sm: "0.875rem", // 14px - Secondary text, labels
    md: "1rem", // 16px - Body text (base)
    lg: "1.125rem", // 18px - Emphasized body
    xl: "1.25rem", // 20px - Subheadings
    "2xl": "1.5rem", // 24px - Section titles
    "3xl": "2rem", // 32px - Page titles
    "4xl": "2.5rem", // 40px - Hero text
    "5xl": "3.25rem", // 52px - Large hero
    "6xl": "4rem", // 64px - Extra large display

    // Semantic aliases for headings
    h1: "2.5rem", // 40px - Page titles
    h2: "2rem", // 32px - Section headers
    h3: "1.5rem", // 24px - Card titles
    h4: "1.25rem", // 20px - Subsections
    h5: "1rem", // 16px - Small headings
    h6: "0.875rem", // 14px - Micro headings

    // Data-specific sizes
    stat: "2.5rem", // 40px - Dashboard statistics
    statLarge: "3.5rem", // 56px - Hero statistics
    currency: "1.125rem", // 18px - Money values
    odometer: "1.5rem", // 24px - Mileage displays
  },

  // ============================================
  // Font Weights
  // Use extremes for impact: 200 vs 800
  // ============================================
  weights: {
    thin: 200, // For large display text contrast
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800, // For headings, stats
    black: 900, // For maximum impact
  },

  // ============================================
  // Line Heights
  // ============================================
  lineHeights: {
    none: 1, // Stats, single-line displays
    tight: 1.15, // Headings
    snug: 1.25, // Subheadings
    normal: 1.5, // Body text
    relaxed: 1.625, // Long-form content
    loose: 1.75, // Spacious reading
    double: 2, // Extra spacious
  },

  // ============================================
  // Letter Spacing
  // ============================================
  letterSpacing: {
    tighter: "-0.05em", // Tight headlines
    tight: "-0.025em", // Headings
    normal: "0", // Body
    wide: "0.025em", // Labels
    wider: "0.05em", // Uppercase labels
    widest: "0.1em", // Spaced caps
  },
} as const;

// ============================================
// Text Style Presets
// Common combinations for consistent styling
// ============================================
export const textStyles = {
  // ============================================
  // Body text variants
  // ============================================
  bodySmall: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  bodyDefault: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.md,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  bodyLarge: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.lg,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  // ============================================
  // UI text
  // ============================================
  label: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
    letterSpacing: fonts.letterSpacing.wide,
  },

  labelUppercase: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.xs,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
    letterSpacing: fonts.letterSpacing.wider,
    textTransform: "uppercase" as const,
  },

  caption: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.xs,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  // ============================================
  // Button text
  // ============================================
  button: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.md,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
    letterSpacing: fonts.letterSpacing.wide,
  },

  buttonSmall: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
  },

  buttonLarge: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.lg,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
  },

  // ============================================
  // Headings (using Bricolage Grotesque)
  // ============================================
  heading1: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h1,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.extrabold,
    letterSpacing: fonts.letterSpacing.tight,
  },

  heading2: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h2,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.bold,
    letterSpacing: fonts.letterSpacing.tight,
  },

  heading3: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h3,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.bold,
  },

  heading4: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h4,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
  },

  heading5: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h5,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.semibold,
  },

  heading6: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h6,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.semibold,
  },

  // ============================================
  // Display text (hero sections)
  // ============================================
  displayLarge: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes["6xl"],
    lineHeight: fonts.lineHeights.none,
    fontWeight: fonts.weights.black,
    letterSpacing: fonts.letterSpacing.tighter,
  },

  displayMedium: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes["5xl"],
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.extrabold,
    letterSpacing: fonts.letterSpacing.tighter,
  },

  displaySmall: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes["4xl"],
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.extrabold,
    letterSpacing: fonts.letterSpacing.tight,
  },

  // ============================================
  // Data/Numeric displays (using JetBrains Mono)
  // ============================================
  stat: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.stat,
    lineHeight: fonts.lineHeights.none,
    fontWeight: fonts.weights.bold,
    letterSpacing: fonts.letterSpacing.tight,
    fontFeatureSettings: '"tnum" 1, "lnum" 1', // Tabular, lining numbers
  },

  statLarge: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.statLarge,
    lineHeight: fonts.lineHeights.none,
    fontWeight: fonts.weights.bold,
    letterSpacing: fonts.letterSpacing.tighter,
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
  },

  currency: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.currency,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
  },

  odometer: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.odometer,
    lineHeight: fonts.lineHeights.none,
    fontWeight: fonts.weights.semibold,
    letterSpacing: fonts.letterSpacing.normal,
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
  },

  // ============================================
  // Code/Technical text
  // ============================================
  code: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  codeBlock: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.relaxed,
    fontWeight: fonts.weights.normal,
  },

  // ============================================
  // Navigation
  // ============================================
  navItem: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
  },

  navItemActive: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
  },

  // ============================================
  // Table/Data grid
  // ============================================
  tableHeader: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.xs,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
    letterSpacing: fonts.letterSpacing.wider,
    textTransform: "uppercase" as const,
  },

  tableCell: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.normal,
  },

  tableCellNumeric: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.normal,
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
  },
} as const;

// ============================================
// Google Fonts Import URL
// Include in your HTML head or CSS
// ============================================
export const googleFontsUrl =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200..800&family=JetBrains+Mono:wght@400;500;600;700&family=Manrope:wght@300;400;500;600;700;800&display=swap";

// ============================================
// Font Loading CSS (for _document or index.html)
// ============================================
export const fontFaceCSS = `
@import url('${googleFontsUrl}');
`;
